import { useTranslation } from '../../i18n';
import React, { useState, useEffect } from 'react';
import CountryFlag, { countryFromRegion } from '../../components/CountryFlag';
import {
    Card,
    Tabs,
    Descriptions,
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Row,
    Col,
    Statistic,
    Progress,
    Alert,
    Select,
    Switch,
    InputNumber
} from 'antd';
import {
    GlobalOutlined,
    DatabaseOutlined,
    TeamOutlined,
    ClockCircleOutlined,
    SafetyCertificateOutlined,
    FolderOpenOutlined,
    SettingOutlined,
    ReloadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    LockOutlined,
    UnlockOutlined,
    FileProtectOutlined,
    LinkOutlined,
    FileTextOutlined,
    OrderedListOutlined,
    UploadOutlined,
    BarChartOutlined,
    ToolOutlined,
    AppstoreOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import hostSharedService from '../../../shared/services/hostSharedService';
import FileManager from './components/FileManager';
import BackupManager from './components/BackupManager';
import CronManager from './components/CronManager';
import PerformanceSettings from './components/PerformanceSettings';
import SecuritySettings from './components/SecuritySettings';
import AntiUploadManager from './components/AntiUploadManager';
import HotlinkManager from './components/HotlinkManager';
import HttpAuthManager from './components/HttpAuthManager';
import IndexFileManager from './components/IndexFileManager';
import RewriteManager from './components/RewriteManager';
import AdvancedSettings from './components/AdvancedSettings';
import FlowStatistics from './components/FlowStatistics';
import ShellTools from './components/ShellTools';
import WebappManager from './components/WebappManager';
import AppStore from './components/AppStore';
import LetsEncryptPanel from './components/LetsEncryptPanel';
import SiteLogs from './components/SiteLogs';

import { userPath, USER_BASE_PATH, ADMIN_BASE_PATH } from '../../../shared/config/paths';

const { TextArea } = Input;
const { Option } = Select;

/** show 接口把用量放在 quota/domains/databases 里；概览卡片用的平铺字段在这里补齐。 */
const normalizeVhost = (v) => {
    if (!v || typeof v !== 'object') return v;
    const q = v.quota || {};
    const domains = Array.isArray(v.domains) ? v.domains : [];
    const pick = (...xs) => xs.find((x) => x !== undefined && x !== null && x !== '');
    return {
        ...v,
        vhost_id: pick(v.vhost_id, v.id),
        domain: pick(v.domain, (domains.find((d) => d.is_primary || d.type === 'primary') || domains[0] || {}).domain),
        disk_quota: pick(v.disk_quota, q.disk_quota, 0),
        disk_usage: pick(v.disk_usage, q.disk_used, 0),
        bandwidth_quota: pick(v.bandwidth_quota, q.bandwidth_quota, 0),
        bandwidth_usage: pick(v.bandwidth_usage, q.bandwidth_used, 0),
        domains_count: pick(v.domains_count, v.stats?.domains_count, domains.length),
        domain_quota: pick(v.domain_quota, q.domain_quota, 0),
        databases_count: pick(v.databases_count, v.stats?.databases_count, Array.isArray(v.databases) ? v.databases.length : 0),
        db_quota_mb: pick(q.db_quota, 0),
    };
};

const VhostDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [vhost, setVhost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    // Domains
    const [domains, setDomains] = useState([]);
    const [domainModalVisible, setDomainModalVisible] = useState(false);
    const [editingDomain, setEditingDomain] = useState(null);
    const [domainForm] = Form.useForm();

    // Databases
    const [databaseInfo, setDatabaseInfo] = useState(null);
    const [databaseModalVisible, setDatabaseModalVisible] = useState(false);
    const [databaseForm] = Form.useForm();

    // FTP
    const [ftpInfo, setFtpInfo] = useState(null);
    const [ftpModalVisible, setFtpModalVisible] = useState(false);
    const [ftpForm] = Form.useForm();
    const [ftpPasswordVisible, setFtpPasswordVisible] = useState(false);
    const [ftpPasswordForm] = Form.useForm();

    // SSL
    const [sslCerts, setSslCerts] = useState([]);
    const [sslModalVisible, setSslModalVisible] = useState(false);
    const [sslForm] = Form.useForm();

    useEffect(() => {
        if (id) {
            loadVhost();
        }
    }, [id]);

    useEffect(() => {
        if (activeTab === 'domains') {
            loadDomains();
        } else if (activeTab === 'databases') {
            loadDatabaseInfo();
        } else if (activeTab === 'ftp') {
            loadFtpInfo();
        } else if (activeTab === 'ssl') {
            loadDomains();
            loadSslCerts();
        }
    }, [activeTab, vhost?.name]);

    const loadVhost = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getVhost(id);
            if (response.success) {
                setVhost(normalizeVhost(response.data));
                // 同时加载数据库和FTP信息用于状态显示
                const vhostName = response.data?.name;
                if (vhostName) {
                    loadDatabaseInfoByName(vhostName);
                    loadFtpInfoByName(vhostName);
                }
            } else {
                message.error(response.error || t('Failed to retrieve hosting information'));
                navigate(userPath('/host_shared/vhosts'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve hosting information'));
        } finally {
            setLoading(false);
        }
    };

    // ===== Domains =====
    const loadDomains = async () => {
        try {
            const response = await hostSharedService.getDomains(id);
            if (response.success) {
                const domainsData = Array.isArray(response.data)
                    ? response.data.map((item) => ({
                          ...item,
                          type: item.is_primary ? 'primary' : (item.type || 'alias'),
                          document_root: item.document_root || '/',
                          proto: item.proto || 'http'
                      }))
                    : [];
                setDomains(domainsData);
            } else {
                setDomains([]);
            }
        } catch (error) {
            console.error('获取域名列表失败:', error);
            setDomains([]);
        }
    };

    const handleAddOrUpdateDomain = async (values) => {
        try {
            const payload = {
                ...values,
                type: values.type || 'alias',
                proto: values.proto || 'http',
                document_root: values.document_root || '/'
            };

            let response;
            if (editingDomain && editingDomain.id) {
                response = await hostSharedService.updateDomain(id, editingDomain.id, payload);
            } else {
                response = await hostSharedService.addDomain(id, payload);
            }

            if (response.success) {
                message.success(editingDomain ? t('Domain updated successfully') : t('Domain added successfully'));
                setDomainModalVisible(false);
                setEditingDomain(null);
                domainForm.resetFields();
                loadDomains();
            } else {
                message.error(response.error || (editingDomain ? t('Update failed') : t('Failed to add')));
            }
        } catch (error) {
            message.error((editingDomain ? t('Update failed: ') : t('Failed to add: ')) + error.message);
        }
    };

    const handleDeleteDomain = async (domainId) => {
        try {
            const response = await hostSharedService.deleteDomain(id, domainId);
            if (response.success) {
                message.success(t('Domain deleted successfully'));
                loadDomains();
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    // ===== Databases =====
    const loadDatabaseInfoByName = async (vhostName) => {
        try {
            const response = await hostSharedService.getDatabaseInfo(vhostName);
            if (response.success && response.data) {
                setDatabaseInfo(response.data);
            } else {
                setDatabaseInfo(null);
            }
        } catch (error) {
            console.error('获取数据库信息失败:', error);
            setDatabaseInfo(null);
        }
    };

    const loadDatabaseInfo = async () => {
        if (!vhost?.name) return;
        loadDatabaseInfoByName(vhost.name);
    };

    const handleChangeDatabasePassword = async (values) => {
        try {
            // 只改 MySQL 账号密码(vhost_api db_password_change)
            const response = await hostSharedService.changeDatabasePassword(id, values.password);
            if (response.success) {
                message.success(t('Database password changed'));
                setDatabaseModalVisible(false);
                databaseForm.resetFields();
                loadDatabaseInfo();
            } else {
                message.error(response.error || t('Failed to change password'));
            }
        } catch (error) {
            message.error(t('Failed to change password'));
        }
    };

    // ===== FTP =====
    const loadFtpInfoByName = async (vhostName) => {
        try {
            const response = await hostSharedService.getFtpConfig(vhostName);
            if (response.success && response.data) {
                setFtpInfo(response.data);
            } else {
                setFtpInfo(null);
            }
        } catch (error) {
            console.error('获取FTP信息失败:', error);
            setFtpInfo(null);
        }
    };

    const loadFtpInfo = async () => {
        if (!vhost?.name) return;
        loadFtpInfoByName(vhost.name);
    };

    const handleChangeFtpPassword = async (values) => {
        const response = await hostSharedService.changeFtpPassword(id, values.password);
        if (response.success) {
            message.success(t('FTP password changed'));
            setFtpPasswordVisible(false);
            ftpPasswordForm.resetFields();
        } else {
            message.error(response.error || t('Failed to change password'));
        }
    };

    const handleUpdateFtpConfig = async (values) => {
        try {
            // 更新FTP主目录
            if (values.ftp_dir !== undefined) {
                const configResponse = await hostSharedService.setFtpConfig(
                    vhost?.name,
                    ftpInfo?.enabled || ftpInfo?.ftp === 1 ? 1 : 0,
                    values.ftp_dir
                );
                if (!configResponse.success) {
                    message.error(configResponse.error || t('Failed to set home directory'));
                    return;
                }
            }

            message.success(t('FTP home directory updated'));

            setFtpModalVisible(false);
            ftpForm.resetFields();
            loadFtpInfoByName(vhost?.name);
        } catch (error) {
            message.error(t('Failed to update configuration: {{message}}', { message: error.message }));
        }
    };

    const handleToggleFtp = async (vhostName, enabled) => {
        try {
            const response = await hostSharedService.setFtpConfig(vhostName, enabled ? 1 : 0, ftpInfo?.ftp_dir || '');
            if (response.success) {
                message.success(enabled ? t('FTP enabled') : t('FTP disabled'));
                loadFtpInfoByName(vhostName);
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    // ===== SSL =====
    const loadSslCerts = async () => {
        try {
            const response = await hostSharedService.getSslCerts();
            if (response.success) {
                setSslCerts(response.data || []);
            }
        } catch (error) {
            console.error('获取SSL证书列表失败:', error);
        }
    };

    const handleDeploySsl = async (domainId, certId) => {
        try {
            const response = await hostSharedService.deploySsl(id, domainId, certId);
            if (response.success) {
                message.success(t('SSL certificate deployed'));
                loadDomains();
            } else {
                message.error(response.error || t('Deployment failed'));
            }
        } catch (error) {
            message.error(t('Deployment failed'));
        }
    };

    const handleUploadSsl = async () => {
        try {
            const values = await sslForm.validateFields();
            setLoading(true);
            const response = await hostSharedService.setSslFull(
                vhost?.name,
                values.certificate,
                values.certificate_key
            );
            if (response.success) {
                message.success(t('SSL certificate uploaded'));
                setSslModalVisible(false);
                sslForm.resetFields();
                loadVhost(); // 重新加载虚拟主机信息以更新ssl_enabled状态
            } else {
                message.error(response.error || t('Upload failed'));
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(t('Upload failed: {{message}}', { message: error.message }));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleToggleForceHttps = async (domainId, enabled) => {
        try {
            const response = await hostSharedService.updateDomainForceHttps(id, domainId, enabled);
            if (response.success) {
                message.success(t('Force HTTPS {{status}}', { status: enabled ? t('Enable') : t('Close') }));
                loadDomains();
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    const getProgressStatus = (percent) => {
        if (percent >= 90) return 'exception';
        if (percent >= 70) return 'normal';
        return 'success';
    };

    if (!vhost) {
        return <Card loading={loading}>{t('Loading...')}</Card>;
    }

    const diskPercent = vhost.disk_quota > 0 ? Math.round((vhost.disk_usage / vhost.disk_quota) * 100) : 0;
    const bandwidthPercent = vhost.bandwidth_quota > 0 ? Math.round((vhost.bandwidth_usage / vhost.bandwidth_quota) * 100) : 0;

    // ===== Tab Items =====
    const tabItems = [
        {
            key: 'overview',
            label: (
                <span>
                    <SettingOutlined />
                    {t('Overview')}
                </span>
            ),
            children: (
                <div>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title={t('Disk usage')}
                                    value={diskPercent}
                                    suffix="%"
                                    valueStyle={{ color: diskPercent >= 90 ? '#cf1322' : '#3f8600' }}
                                />
                                <Progress
                                    percent={diskPercent}
                                    status={getProgressStatus(diskPercent)}
                                    showInfo={false}
                                />
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                    {vhost.disk_usage || 0}MB / {vhost.disk_quota}MB
                                </div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title={t('Bandwidth usage')}
                                    value={bandwidthPercent}
                                    suffix="%"
                                    valueStyle={{ color: bandwidthPercent >= 90 ? '#cf1322' : '#3f8600' }}
                                />
                                <Progress
                                    percent={bandwidthPercent}
                                    status={getProgressStatus(bandwidthPercent)}
                                    showInfo={false}
                                />
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                    {vhost.bandwidth_usage || 0}GB / {vhost.bandwidth_quota}GB
                                </div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title={t('Domains')}
                                    value={vhost.domains_count || 0}
                                    suffix={vhost.domain_quota > 0 ? `/ ${vhost.domain_quota}` : t('/ Unlimited')}
                                    prefix={<GlobalOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title={t('Database')}
                                    value={vhost.databases_count || 0}
                                    suffix={vhost.db_quota_mb > 0 ? <span style={{ fontSize: 12 }}>{`(${vhost.db_quota_mb}MB)`}</span> : null}
                                    prefix={<DatabaseOutlined />}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Card title={t('Basic information')}>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label={t('Hostname')}>{vhost.name}</Descriptions.Item>
                            <Descriptions.Item label={t('Hosting ID')}>{vhost.vhost_id}</Descriptions.Item>
                            <Descriptions.Item label={t('Primary domain')}>{vhost.domain}</Descriptions.Item>
                            <Descriptions.Item label={t('Status')}>
                                <Tag color={vhost.status === 1 ? 'success' : 'error'}>
                                    {vhost.status === 1 ? t('Normal') : t('Suspended')}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Node')}>
                                {vhost.node?.name || t('Node #{{node_id}}', { node_id: vhost.node_id })}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Zone')}>
                                <CountryFlag code={countryFromRegion(vhost.node?.region)} />{vhost.node?.region || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('FTP status')}>
                                {ftpInfo ? (ftpInfo.enabled || ftpInfo.ftp === 1 ? <Tag color="success">{t('Enabled')}</Tag> : <Tag>{t('Disabled')}</Tag>) : <Tag>{t('Not configured')}</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Database status')}>
                                {databaseInfo ? <Tag color="success">{t('Configured')}</Tag> : <Tag>{t('Not configured')}</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Created at')} span={2}>
                                {vhost.created_at ? new Date(vhost.created_at).toLocaleString('zh-CN') : '-'}
                            </Descriptions.Item>
                            {vhost.remark && (
                                <Descriptions.Item label={t('Notes')} span={2}>{vhost.remark}</Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                </div>
            )
        },
        {
            key: 'domains',
            label: (
                <span>
                    <GlobalOutlined />
                    {t('Domain management')}
                </span>
            ),
            children: (
                <Card
                    title={t('Domain list')}
                    extra={
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditingDomain(null);
                                domainForm.resetFields();
                                setDomainModalVisible(true);
                            }}
                        >
                            {t('Add domain')}
                        </Button>
                    }
                >
                    <Table
                        dataSource={Array.isArray(domains) ? domains : []}
                        rowKey={(record) => record.id ?? record.domain}
                        columns={[
                            {
                                title: t('Domains'),
                                dataIndex: 'domain',
                                key: 'domain'
                            },
                            {
                                title: t('Type'),
                                dataIndex: 'type',
                                key: 'type',
                                render: (type, record) => (
                                    <Tag color={type === 'primary' ? 'blue' : (type === 'subdir' ? 'purple' : 'default')}>
                                        {type === 'primary' ? t('Primary domain') : (type === 'subdir' ? t('Subdirectory') : t('Additional domains'))}
                                    </Tag>
                                )
                            },
                            {
                                title: t('Origin protocol'),
                                dataIndex: 'proto',
                                key: 'proto',
                                render: (proto) => (
                                    <Tag>{proto?.toUpperCase()}</Tag>
                                )
                            },
                            {
                                title: t('Bound directory / origin'),
                                dataIndex: 'document_root',
                                key: 'document_root',
                                render: (dir) => dir || '/'
                            },
                            {
                                title: t('SSL status'),
                                dataIndex: 'ssl_enabled',
                                key: 'ssl_enabled',
                                render: (enabled) => (
                                    <Tag color={enabled ? 'success' : 'default'}>
                                        {enabled ? t('Enabled') : t('Disabled')}
                                    </Tag>
                                )
                            },
                            {
                                title: t('Force HTTPS'),
                                dataIndex: 'force_https',
                                key: 'force_https',
                                render: (enabled, record) => (
                                    <Switch
                                        checked={!!enabled}
                                        disabled={!record.ssl_enabled}
                                        onChange={(checked) => handleToggleForceHttps(record.domain, checked)}
                                    />
                                )
                            },
                            {
                                title: t('Actions'),
                                key: 'action',
                                width: 150,
                                render: (_, record) => (
                                    <Space>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => {
                                                setEditingDomain(record);
                                                domainForm.setFieldsValue({
                                                    domain: record.domain,
                                                    document_root: record.document_root || '/',
                                                    proto: record.proto || 'http',
                                                    type: record.type || (record.is_primary ? 'primary' : 'alias')
                                                });
                                                setDomainModalVisible(true);
                                            }}
                                        >
                                            {t('Edit')}
                                        </Button>
                                        {!record.is_primary && record.id && (
                                            <Popconfirm
                                                title={t('Delete this domain?')}
                                                onConfirm={() => handleDeleteDomain(record.id)}
                                            >
                                                <Button size="small" danger icon={<DeleteOutlined />}>
                                                    {t('Delete')}
                                                </Button>
                                            </Popconfirm>
                                        )}
                                    </Space>
                                )
                            }
                        ]}
                    />
                </Card>
            )
        },
        {
            key: 'databases',
            label: (
                <span>
                    <DatabaseOutlined />
                    {t('Manage databases')}
                </span>
            ),
            children: (
                <Card title={t('Database details')}>
                    {databaseInfo ? (
                        <div>
                            <Descriptions bordered column={2}>
                                <Descriptions.Item label={t('Database name')}>{databaseInfo?.db_name || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('Username')}>{databaseInfo?.db_user || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('Database type')}>{databaseInfo?.db_type || 'mysql'}</Descriptions.Item>
                                <Descriptions.Item label={t('Database size')}>
                                    {databaseInfo?.db_used ? `${databaseInfo.db_used}MB` : '0MB'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Quota')}>
                                    {databaseInfo?.db_quota ? `${databaseInfo.db_quota}MB` : t('Unlimited')}
                                </Descriptions.Item>
                                <Descriptions.Item label="phpMyAdmin">
                                    {databaseInfo?.phpmyadmin_url ? (
                                        <a href={databaseInfo.phpmyadmin_url} target="_blank" rel="noopener noreferrer">
                                            {t('Open phpMyAdmin')}
                                        </a>
                                    ) : '-'}
                                </Descriptions.Item>
                            </Descriptions>
                            <div style={{ marginTop: 16 }}>
                                <Space>
                                    <Button
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            databaseForm.setFieldsValue({ password: '' });
                                            setDatabaseModalVisible(true);
                                        }}
                                    >
                                        {t('Change password')}
                                    </Button>
                                    <Button icon={<ReloadOutlined />} onClick={loadDatabaseInfo}>
                                        {t('Refresh information')}
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    ) : (
                        <Alert
                            message={t('No databases yet')}
                            description={t('This hosting account has no database yet. A database is created automatically when the hosting account is created.')}
                            type="info"
                            showIcon
                        />
                    )}
                </Card>
            )
        },
        {
            key: 'ftp',
            label: (
                <span>
                    <TeamOutlined />
                    {t('FTP account')}
                </span>
            ),
            children: (
                <Card title={t('FTP account details')}>
                    {ftpInfo ? (
                        <div>
                            <Descriptions bordered column={2}>
                                <Descriptions.Item label={t('Username')}>{vhost?.name || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('FTP server')}>
                                    {vhost.ftp_host || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Home directory')}>
                                    {ftpInfo?.ftp_dir || '/'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Port')}>21</Descriptions.Item>
                                <Descriptions.Item label={t('Status')}>
                                    <Space>
                                        <Tag color={ftpInfo?.enabled || ftpInfo?.ftp === 1 ? 'success' : 'default'}>
                                            {ftpInfo?.enabled || ftpInfo?.ftp === 1 ? t('Enabled') : t('Disabled')}
                                        </Tag>
                                        <Switch
                                            checked={ftpInfo?.enabled || ftpInfo?.ftp === 1}
                                            onChange={(checked) => handleToggleFtp(vhost?.name, checked)}
                                            checkedChildren={t('Enable')}
                                            unCheckedChildren={t('Disable')}
                                        />
                                    </Space>
                                </Descriptions.Item>
                            </Descriptions>
                            <Alert
                                message={t('Connection details')}
                                description={
                                    <div>
                                        <p><strong>{t('FTP server:')}</strong> {vhost.ftp_host || '-'}</p>
                                        <p><strong>{t('Port:')}</strong> 21</p>
                                        <p><strong>{t('Username:')}</strong>{t(" {{name}} (Fixed, same as the virtual host name)", { name: vhost?.name })}</p>
                                        <p><strong>{t('Password:')}</strong>{t(' Same as the virtual host password')}</p>
                                        <p><strong>{t('Home directory:')}</strong> {ftpInfo?.ftp_dir || '/'}</p>
                                        <p style={{marginTop: 8}}><strong>{t('Recommended FTP tools:')}</strong> FileZilla, WinSCP, CuteFTP</p>
                                    </div>
                                }
                                type="info"
                                showIcon
                                style={{ marginTop: 16, marginBottom: 16 }}
                            />
                            <div style={{ marginTop: 16 }}>
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            ftpForm.setFieldsValue({
                                                ftp_dir: ftpInfo?.ftp_dir || '/'
                                            });
                                            setFtpModalVisible(true);
                                        }}
                                    >
                                        {t('Edit configuration')}
                                    </Button>
                                    <Button
                                        icon={<LockOutlined />}
                                        onClick={() => {
                                            ftpPasswordForm.resetFields();
                                            setFtpPasswordVisible(true);
                                        }}
                                    >
                                        {t('Change password')}
                                    </Button>
                                    <Button icon={<ReloadOutlined />} onClick={loadFtpInfo}>
                                        {t('Refresh information')}
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    ) : (
                        <Alert
                            message={t('No FTP accounts yet')}
                            description={t('This hosting account has no FTP account yet. An FTP account is created automatically when the hosting account is created.')}
                            type="info"
                            showIcon
                        />
                    )}
                </Card>
            )
        },
        {
            key: 'ssl',
            label: (
                <span>
                    <SafetyCertificateOutlined />
                    {t('SSL certificates')}
                </span>
            ),
            children: (
                <Card title={t('SSL certificate management (global)')}>
                    <LetsEncryptPanel vhostId={id} onChanged={loadVhost} />

                    {vhost?.ssl_enabled ? (
                        <div>
                            <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
                                <Descriptions.Item label={t('SSL status')}>
                                    <Tag color="success">{t('Deployed')}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Applicable domains')}>
                                    {t('All bound domains')}
                                </Descriptions.Item>
                            </Descriptions>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    onClick={() => setSslModalVisible(true)}
                                >
                                    {t('Update certificate')}
                                </Button>
                            </Space>
                        </div>
                    ) : (
                        <div>
                            <Alert
                                message={t('No SSL certificate deployed')}
                                description={t('You have not deployed an SSL certificate for this shared hosting account yet. Click the button below to upload a certificate and private key.')}
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={() => setSslModalVisible(true)}
                            >
                                {t('Upload SSL certificate')}
                            </Button>
                        </div>
                    )}
                </Card>
            )
        },
        // ===== 一键应用 Tab =====
        {
            key: 'webapp',
            label: (
                <span>
                    <AppstoreOutlined />
                    {t('App store')}
                </span>
            ),
            // user console: app store with headless installs; admin view keeps the legacy EP installer
            children: (USER_BASE_PATH === null || (ADMIN_BASE_PATH && window.location.pathname.startsWith(ADMIN_BASE_PATH + '/')))
                ? <WebappManager vhostName={vhost?.name} vhostId={id} />
                : <AppStore vhostId={id} domains={domains} />
        },
        // ===== 文件管理 Tab =====
        {
            key: 'files',
            label: (
                <span>
                    <FolderOpenOutlined />
                    {t('File manager')}
                </span>
            ),
            children: <FileManager vhostId={id} vhostName={vhost?.name} />
        },
        // ===== 备份管理 Tab =====
        {
            key: 'backups',
            label: (
                <span>
                    <ClockCircleOutlined />
                    {t('Backup management')}
                </span>
            ),
            children: <BackupManager vhostId={id} vhostName={vhost?.name}
                adminView={USER_BASE_PATH === null || (ADMIN_BASE_PATH && window.location.pathname.startsWith(ADMIN_BASE_PATH + '/'))} />
        },
        // ===== 计划任务 Tab =====
        {
            key: 'crons',
            label: (
                <span>
                    <ClockCircleOutlined />
                    {t('Scheduled tasks')}
                </span>
            ),
            children: <CronManager vhostId={id} vhostName={vhost?.name} />
        },
        // ===== 性能优化 Tab =====
        {
            key: 'performance',
            label: (
                <span>
                    <SettingOutlined />
                    {t('Performance optimization')}
                </span>
            ),
            children: <PerformanceSettings vhostId={id} vhostName={vhost?.name} />
        },
        {
            key: 'flow',
            label: (
                <span>
                    <BarChartOutlined />
                    {t('Traffic statistics')}
                </span>
            ),
            children: <FlowStatistics vhostName={vhost?.name} />
        },
        // ===== 站点日志 Tab(用户控制台) =====
        ...((USER_BASE_PATH === null || (ADMIN_BASE_PATH && window.location.pathname.startsWith(ADMIN_BASE_PATH + '/'))) ? [] : [{
            key: 'logs',
            label: (
                <span>
                    <FileTextOutlined />
                    {t('Logs')}
                </span>
            ),
            children: <SiteLogs vhostId={id} />
        }]),
        {
            key: 'ops',
            label: (
                <span>
                    <ToolOutlined />
                    {t('Operations tools')}
                </span>
            ),
            children: <ShellTools vhostName={vhost?.name} />
        },
        // ===== 安全设置 Tab =====
        {
            key: 'security',
            label: (
                <span>
                    <LockOutlined />
                    {t('Security settings')}
                </span>
            ),
            children: <SecuritySettings vhostId={id} vhostName={vhost?.name} />
        },
        // ===== 防木马上传 Tab =====
        {
            key: 'antiupload',
            label: (
                <span>
                    <FileProtectOutlined />
                    {t('Malware upload protection')}
                </span>
            ),
            children: <AntiUploadManager vhostName={vhost?.name} />
        },
        // ===== 防盗链 Tab =====
        {
            key: 'hotlink',
            label: (
                <span>
                    <LinkOutlined />
                    {t('Hotlink protection')}
                </span>
            ),
            children: <HotlinkManager vhostName={vhost?.name} />
        },
        // ===== HTTP认证 Tab =====
        {
            key: 'httpauth',
            label: (
                <span>
                    <LockOutlined />
                    {t('HTTP authentication')}
                </span>
            ),
            children: <HttpAuthManager vhostName={vhost?.name} />
        },
        // ===== 默认首页 Tab =====
        {
            key: 'indexfile',
            label: (
                <span>
                    <OrderedListOutlined />
                    {t('Default index page')}
                </span>
            ),
            children: <IndexFileManager vhostName={vhost?.name} />
        },
        // ===== 伪静态 Tab =====
        {
            key: 'rewrite',
            label: (
                <span>
                    <FileTextOutlined />
                    {t('URL rewriting')}
                </span>
            ),
            children: <RewriteManager vhostName={vhost?.name} />
        },
        // ===== 高级配置 Tab =====
        {
            key: 'advanced',
            label: (
                <span>
                    <SettingOutlined />
                    {t('Advanced configuration')}
                </span>
            ),
            children: <AdvancedSettings vhostName={vhost?.name} />
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>{t("Hosting management - {{name}}", { name: vhost.name })}</h2>
                <Button onClick={() => navigate(userPath('/host_shared/vhosts'))}>
                    {t('Back to list')}
                </Button>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />

            {/* 添加/编辑域名模态框 */}
            <Modal
                title={editingDomain ? t('Edit domain') : t('Add domain')}
                open={domainModalVisible}
                onCancel={() => {
                    setDomainModalVisible(false);
                    setEditingDomain(null);
                    domainForm.resetFields();
                }}
                onOk={() => domainForm.submit()}
            >
                <Form form={domainForm} layout="vertical" onFinish={handleAddOrUpdateDomain}>
                    <Form.Item
                        name="domain"
                        label={t('Domains')}
                        rules={[
                            { required: true, message: t('Please enter a domain') },
                            { pattern: /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/, message: t('Enter a valid domain') }
                        ]}
                    >
                        <Input placeholder={t('E.g. www.example.com')} />
                    </Form.Item>
                    <Form.Item
                        name="type"
                        label={t('Type')}
                        initialValue="alias"
                    >
                        <Select disabled={editingDomain?.type === 'primary'}>
                            <Select.Option value="alias">{t('Additional domains')}</Select.Option>
                            <Select.Option value="subdir">{t('Subdirectory')}</Select.Option>
                            {editingDomain?.type === 'primary' && (
                                <Select.Option value="primary">{t('Primary domain')}</Select.Option>
                            )}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="document_root"
                        label={t('Bind directory')}
                        rules={[{ required: true, message: t('Enter the mount directory') }]}
                        initialValue="/"
                        extra={t('Path relative to the virtual host\'s root directory, e.g., / or /public')}
                    >
                        <Input placeholder="/" />
                    </Form.Item>
                    <Form.Item
                        name="proto"
                        label={t('Origin protocol')}
                        initialValue="http"
                        extra={t('CDN hosts support origin protocol settings (HTTP/HTTPS/TCP/match request). Select http for standard hosts.')}
                    >
                        <Select>
                            <Select.Option value="http">HTTP</Select.Option>
                            <Select.Option value="https">HTTPS</Select.Option>
                            <Select.Option value="tcp">TCP</Select.Option>
                            <Select.Option value="follow">{t('Follow request (HTTP/HTTPS)')}</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 修改数据库密码模态框 */}
            <Modal
                title={t('Change database password')}
                open={databaseModalVisible}
                onCancel={() => {
                    setDatabaseModalVisible(false);
                    databaseForm.resetFields();
                }}
                onOk={() => databaseForm.submit()}
            >
                <Alert
                    message={t('Password change instructions')}
                    description={
                        <div>
                            <p>{t('Database username: ')}<strong>{databaseInfo?.db_user || vhost?.name}</strong>{t(' (Fixed, cannot be changed)')}</p>
                            <p>{t('Update the database password in your website configuration after changing it.')}</p>
                        </div>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={databaseForm} layout="vertical" onFinish={handleChangeDatabasePassword}>
                    <Form.Item
                        name="password"
                        label={t('New password')}
                        rules={[
                            { required: true, message: t('Please enter a new password') },
                            { min: 8, max: 64, message: t('8-64 characters') },
                            { pattern: /^[\x21-\x7e]+$/, message: t('No spaces, quotes or backslashes') },
                            { validator: (_, v) => (v && /['"\\`]/.test(v) ? Promise.reject(new Error(t('No spaces, quotes or backslashes'))) : Promise.resolve()) }
                        ]}
                    >
                        <Input.Password placeholder={t('8-64 characters')} maxLength={64} />
                    </Form.Item>
                    <Form.Item
                        name="password_confirmation"
                        label={t('Confirm password')}
                        dependencies={['password']}
                        rules={[
                            { required: true, message: t('Please confirm your password') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('Passwords do not match')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder={t('Re-enter password')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* FTP配置模态框 */}
            <Modal
                title={t('FTP settings')}
                open={ftpModalVisible}
                onCancel={() => {
                    setFtpModalVisible(false);
                    ftpForm.resetFields();
                }}
                onOk={() => ftpForm.submit()}
            >
                <Alert
                    message={t('FTP setup guide')}
                    description={
                        <div>
                            <p>{t('FTP username: ')}<strong>{vhost?.name}</strong>{t(' (Fixed, cannot be changed)')}</p>
                        </div>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={ftpForm} layout="vertical" onFinish={handleUpdateFtpConfig}>
                    <Form.Item
                        name="ftp_dir"
                        label={t('Home directory')}
                        rules={[{ required: true, message: t('Please enter the home directory') }]}
                    >
                        <Input placeholder={t('Path relative to the virtual host\'s root directory, e.g., /')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* FTP 密码 = 站点面板登录密码 */}
            <Modal
                title={t('Change FTP password')}
                open={ftpPasswordVisible}
                onCancel={() => {
                    setFtpPasswordVisible(false);
                    ftpPasswordForm.resetFields();
                }}
                onOk={() => ftpPasswordForm.submit()}
            >
                <p style={{ marginBottom: 16 }}>{t('Changes the password for FTP and the site panel login')}</p>
                <Form form={ftpPasswordForm} layout="vertical" onFinish={handleChangeFtpPassword}>
                    <Form.Item
                        name="password"
                        label={t('New password')}
                        rules={[
                            { required: true, message: t('Please enter a new password') },
                            { min: 8, max: 64, message: t('8-64 characters') },
                            { pattern: /^[\x21-\x7e]+$/, message: t('No spaces, quotes or backslashes') },
                            { validator: (_, v) => (v && /['"\\`]/.test(v) ? Promise.reject(new Error(t('No spaces, quotes or backslashes'))) : Promise.resolve()) }
                        ]}
                    >
                        <Input.Password placeholder={t('8-64 characters')} maxLength={64} />
                    </Form.Item>
                    <Form.Item
                        name="password_confirmation"
                        label={t('Confirm password')}
                        dependencies={['password']}
                        rules={[
                            { required: true, message: t('Please confirm your password') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('Passwords do not match')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder={t('Re-enter password')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* SSL Certificate Upload Modal */}
            <Modal
                title={t('Manually upload SSL certificate')}
                open={sslModalVisible}
                onOk={handleUploadSsl}
                onCancel={() => {
                    setSslModalVisible(false);
                    sslForm.resetFields();
                }}
                confirmLoading={loading}
                width={800}
                okText={t('Upload')}
                cancelText={t('Cancel')}
            >
                <Alert
                    message={t('Please paste the complete certificate content')}
                    description={t('The certificate must be in PEM format and include the -----BEGIN CERTIFICATE----- and -----END CERTIFICATE----- markers. The private key must also be in PEM format.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={sslForm} layout="vertical">
                    <Form.Item
                        label={t('SSL certificate (Certificate)')}
                        name="certificate"
                        rules={[
                            { required: true, message: t('Please enter the SSL certificate content') },
                            {
                                validator: (_, value) => {
                                    if (value && value.includes('BEGIN CERTIFICATE')) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('Enter a valid PEM certificate')));
                                }
                            }
                        ]}
                        extra={t('Include -----BEGIN CERTIFICATE----- and -----END CERTIFICATE-----')}
                    >
                        <TextArea
                            rows={10}
                            placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDXTCCAkWgAwIBAgIJAKZ...&#10;-----END CERTIFICATE-----"
                            style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t('Private key')}
                        name="certificate_key"
                        rules={[
                            { required: true, message: t('Enter the private key contents') },
                            {
                                validator: (_, value) => {
                                    if (value && (value.includes('BEGIN PRIVATE KEY') || value.includes('BEGIN RSA PRIVATE KEY'))) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('Enter a valid PEM private key')));
                                }
                            }
                        ]}
                        extra={t('Include -----BEGIN PRIVATE KEY----- or -----BEGIN RSA PRIVATE KEY-----')}
                    >
                        <TextArea
                            rows={10}
                            placeholder="-----BEGIN PRIVATE KEY-----&#10;MIIEvQIBADANBgkqhkiG9w0...&#10;-----END PRIVATE KEY-----"
                            style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default VhostDetail;
